import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Search } from "lucide-react";
import { Dialog as DialogPrimitive } from "radix-ui";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { PERMISSIONS } from "@/api/permissions";
import { SEARCH_MIN_LENGTH, userSearchQuery } from "@/api/queries/users";
import { useHasPermission, useWhoami } from "@/auth/use-whoami";
import { Kbd } from "@/components/Kbd";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { PALETTE_PAGES } from "@/lib/nav";
import { rememberRecentUser } from "@/lib/recent-users";
import { useDebouncedValue } from "@/lib/use-debounced-value";
import { isMac } from "@/lib/use-keyboard-shortcuts";
import { cn } from "@/lib/utils";

/**
 * The ⌘K palette: a Spotlight-style search that floats below the top bar —
 * the header stays visible and the backdrop starts under it, so the
 * operator never loses their place. Users (email · name · phone · id, for
 * roles holding USERS_READ) and pages (the nav the role can see) in one
 * list, each page captioned with its sidebar group; Enter opens the
 * highlighted row, "Search all users" goes to /users.
 *
 * Open with ⌘K / Ctrl+K anywhere, or "/" outside an input; the header's
 * search box is the mouse trigger. State lives in the body component, which
 * is mounted inside the Radix content so it resets on close via unmount.
 *
 * Composed from the Radix Dialog primitive rather than the vendored
 * `DialogContent`, whose overlay covers the whole viewport.
 */
export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable === true;
      if (
        ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") ||
        (event.key === "/" && !typing && !open)
      ) {
        // preventDefault also tells useKeyboardShortcuts to leave this alone.
        event.preventDefault();
        onOpenChange(!open || event.key === "/");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onOpenChange]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        {/* Starts under the h-14 header so the top bar stays visible. */}
        {/* No exit animation on the overlay/content: Radix keeps the node
            mounted until an exit animation ends, and a close triggered from
            inside cmdk's keydown handling never got one to finish — the
            palette would stay stuck on screen. Instant close, like Spotlight. */}
        <DialogPrimitive.Overlay className="data-open:animate-in data-open:fade-in-0 fixed inset-x-0 top-14 bottom-0 z-40 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="bg-popover text-popover-foreground ring-foreground/10 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 fixed top-[calc(3.5rem+8vh)] left-1/2 z-50 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl shadow-lg ring-1 duration-100 outline-none"
        >
          <DialogPrimitive.Title className="sr-only">
            Search users and pages
          </DialogPrimitive.Title>
          <PaletteBody close={() => onOpenChange(false)} />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function PaletteBody({ close }: { close: () => void }) {
  const navigate = useNavigate();
  const { data: whoami } = useWhoami();
  const hasPermission = useHasPermission();
  const canSearchUsers = hasPermission(PERMISSIONS.USERS_READ);

  const [value, setValue] = useState("");
  const debounced = useDebouncedValue(value, 300);
  const term = debounced.trim();
  const active = canSearchUsers && term.length >= SEARCH_MIN_LENGTH;
  const search = useQuery({
    ...userSearchQuery(debounced),
    enabled: active,
  });
  const users = active ? (search.data?.users ?? []) : [];

  // Pages: everything the role can open — the sidebar plus the drill-downs
  // that are deliberately not in it. Filtered once the operator types, so
  // "rev" narrows to Reviews without hiding the users. The group name and
  // the per-page keywords match too, so "queue" surfaces Reviews, KYC and
  // Crypto, and "fx" surfaces Balances.
  const query = value.trim().toLowerCase();
  const pages = PALETTE_PAGES.filter((item) => {
    if (!(whoami?.permissions.includes(item.permission) ?? false)) return false;
    if (query.length === 0) return true;
    return [item.label, item.section ?? "", ...(item.keywords ?? [])].some(
      (term) => term.toLowerCase().includes(query),
    );
  });

  const go = (to: string) => {
    close();
    void navigate(to);
  };

  const typedEnough = value.trim().length >= SEARCH_MIN_LENGTH;
  const searching = active && search.isPending;
  const nothing =
    pages.length === 0 && users.length === 0 && !searching && !typedEnough;

  return (
    <Command shouldFilter={false} loop className="rounded-xl">
      <CommandInput
        value={value}
        onValueChange={setValue}
        placeholder={
          canSearchUsers
            ? "Search users by email, name, phone or id — or jump to a page"
            : "Jump to a page"
        }
        aria-label="Search"
      />
      <CommandList className="max-h-[min(60vh,28rem)]">
        {nothing && <CommandEmpty>Nothing to show.</CommandEmpty>}

        {canSearchUsers && (typedEnough || searching) && (
          <CommandGroup heading="Users">
            {searching ? (
              <p className="text-muted-foreground px-2 py-3 text-xs">
                Searching…
              </p>
            ) : search.isError ? (
              <p className="text-destructive px-2 py-3 text-xs">
                {search.error instanceof Error
                  ? search.error.message
                  : "Search failed"}
              </p>
            ) : users.length === 0 && active ? (
              <p className="text-muted-foreground px-2 py-3 text-xs">
                No users match “{term}”
              </p>
            ) : null}
            {users.map((user) => (
              <CommandItem
                key={user.id}
                value={`user:${user.id}`}
                onSelect={() => {
                  rememberRecentUser(user);
                  go(`/users/${user.id}`);
                }}
                className="gap-3"
              >
                <span
                  aria-hidden
                  className={cn(
                    "size-1.5 shrink-0 rounded-full",
                    user.isBanned ? "bg-destructive" : "bg-primary",
                  )}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm">
                    {[user.firstName, user.lastName]
                      .filter(Boolean)
                      .join(" ") || "—"}
                  </span>
                  <span className="text-muted-foreground block truncate font-mono text-xs">
                    {user.email}
                  </span>
                </span>
                {user.nationality && (
                  <span className="text-muted-foreground font-mono text-[11px]">
                    {user.nationality}
                  </span>
                )}
                {user.totalBalance !== null && (
                  <span className="text-muted-foreground font-mono text-[11px] tabular-nums">
                    {user.totalBalance.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </span>
                )}
              </CommandItem>
            ))}
            {active && !searching && (
              <CommandItem
                value="users:search-all"
                onSelect={() =>
                  go(`/users?q=${encodeURIComponent(value.trim())}`)
                }
                className="text-muted-foreground"
              >
                <Search className="size-3.5" />
                Search all users for “{value.trim()}”
                <ArrowRight className="ml-auto size-3.5" />
              </CommandItem>
            )}
          </CommandGroup>
        )}

        {canSearchUsers && (typedEnough || searching) && pages.length > 0 && (
          <CommandSeparator />
        )}

        {pages.length > 0 && (
          <CommandGroup heading="Pages">
            {pages.map((item) => (
              <CommandItem
                key={item.to}
                value={`page:${item.to}`}
                onSelect={() => go(item.to)}
              >
                <item.icon className="size-4" />
                {item.label}
                {/* The sidebar group, so a flat result list still says where
                    a page lives. Pinned pages have no group and no caption.
                    CommandShortcut is the trailing slot: a plain ml-auto span
                    would split the free space with CommandItem's own hidden
                    check icon and land mid-row. */}
                {item.section && (
                  <CommandShortcut className="text-[11px] tracking-normal">
                    {item.section}
                  </CommandShortcut>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
      <div className="text-muted-foreground flex items-center gap-3 border-t px-3 py-1.5 text-[11px]">
        <span className="inline-flex items-center gap-1">
          <Kbd>↑</Kbd>
          <Kbd>↓</Kbd> navigate
        </span>
        <span className="inline-flex items-center gap-1">
          <Kbd>↵</Kbd> open
        </span>
        <span className="inline-flex items-center gap-1">
          <Kbd>esc</Kbd> close
        </span>
        <span className="ml-auto inline-flex items-center gap-1">
          <Kbd>{isMac ? "⌘" : "Ctrl"}</Kbd>
          <Kbd>K</Kbd>
        </span>
      </div>
    </Command>
  );
}
